import TimeIcon from "@mui/icons-material/AccessTimeRounded";
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import type { MembersChangeRequestView } from "../../../../api/types/membersChangeRequestTypes";
import ConfirmChangeRequestCancellationDialog from "../common/ConfirmChangeRequestCancellationDialog";
import { CHANGES_WAITING_FOR_APPROVAL_MESSAGE } from "../common/PendingChangesAlert";
import MemberChangeRequestDetails from "./memberChanges/MemberChangeRequestDetails";
import { getWithdrawChangesHeading } from "./membersChangeRequestHelper";

interface Props {
  open: boolean;
  onClose: () => void;
  canUserEdit: boolean;
  investorId: string;
  changeRequest: MembersChangeRequestView;
  onChangeRequestCanceled: () => void;
}

const MembersChangeRequestDialog = ({
  open,
  onClose,
  canUserEdit,
  investorId,
  changeRequest,
  onChangeRequestCanceled,
}: Props) => {
  const [showCancelConfirmationDialog, setShowCancelConfirmationDialog] = useState(false);

  const workflowId = changeRequest.workflowId;

  const handleChangeRequestCanceled = () => {
    setShowCancelConfirmationDialog(false);
    onChangeRequestCanceled();
  };

  return (
    <>
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle>Request (#{changeRequest.referenceCode})</DialogTitle>
        <DialogCloseButton onClick={onClose} />
        <Divider />
        <DialogContent>
          <Stack spacing={2.5} sx={{ width: "100%", minHeight: "35vh" }}>
            <Alert
              severity="warning"
              iconMapping={{ warning: <TimeIcon fontSize="inherit" /> }}
              sx={{ position: "relative" }}
            >
              <AlertTitle>Pending Changes (#{changeRequest.referenceCode})</AlertTitle>
              {canUserEdit && workflowId !== undefined && (
                <Button
                  variant="text"
                  color="inherit"
                  sx={{ position: "absolute", right: 12, top: 12, py: 0 }}
                  onClick={() => setShowCancelConfirmationDialog(true)}
                >
                  Withdraw Changes
                </Button>
              )}
              <Typography>{CHANGES_WAITING_FOR_APPROVAL_MESSAGE}</Typography>
            </Alert>
            <MemberChangeRequestDetails changeRequest={changeRequest} />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogButtons>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </DialogButtons>
      </Dialog>
      {workflowId !== undefined && (
        <ConfirmChangeRequestCancellationDialog
          investorId={investorId}
          workflowId={workflowId}
          referenceCode={changeRequest.referenceCode}
          open={showCancelConfirmationDialog}
          onClose={() => setShowCancelConfirmationDialog(false)}
          onChangeRequestCanceled={handleChangeRequestCanceled}
          title="Withdraw Changes"
          heading={getWithdrawChangesHeading(changeRequest.referenceCode)}
          description="The changes shown here will be withdrawn from this request."
          confirmLabel="Withdraw Changes"
          successMessage="The changes have been withdrawn"
        />
      )}
    </>
  );
};

export default MembersChangeRequestDialog;
